import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import queryString from 'query-string';
import { DesignLayoutType, PageKind } from '@graphql/generated-base';
import { ColumnsV2, Flex, NoSsr } from '@withjoy/joykit';
import { useHistory } from '@react-router';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import { findEventPageByPageSlug } from '../layout.utils';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import type { LayoutBaseProps } from '../layout.types';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { useScrollToTop } from '@shared/components/ScrollToTop/ScrollToTop';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useFilterMigratedLegacyPages } from '../../hooks/useFilterMigratedLegacyPages';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import FooterNav from '../../components/FooterNav/FooterNav';
import { BrannanDesignProvider } from './BrannanDesignContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';

export interface LayoutBrannanProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  isSinglePage?: boolean;
  setIsLastPageVisible?: (value: boolean) => void;
  setIsPastFirstPage?: (value: boolean) => void;
}

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const LayoutBrannan: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme, hideNav }) => {
  const { info, pages, eventDesign } = event;

  const history = useHistory();

  const isPreviewing = isInIframe();
  const { currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });
  const [useRegistryLinksNav, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');

  // Filter out legacy pages that have been migrated to custom pages
  const { filteredPages, isLoading: isMigratedFlagsLoading } = useFilterMigratedLegacyPages(pages);

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked } = useLayoutBrannanController(filteredPages);

  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });
  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: websiteHeader?.headerPhoto });

  // Create eventProps with filtered pages for navigation menus (including hamburger menu)
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { checkIsWelcomePage, loading: isWelcomePageCheckingLoading } = useWelcomePage();
  const { defaultPage, loading: isDefaultPageLoading } = useDefaultPage(filteredPages);

  // Determine if this is the root route (no slug in URL)
  const isRootRoute = !currentPageSlug;

  const { currentPage, isCustomPage } = useMemo(() => {
    // isCustomPageSlug is already aware of migrated pages when feature flag is ON (via useCurrentPageSlug)
    // For root route, use the default page (welcome page or first page)
    //
    // The fallback lookup (without isCustomPage) handles the transitional state where a migration
    // feature flag is ON but the page hasn't been migrated to type:custom yet. In that case,
    // isCustomPageSlug=true causes the first lookup to miss the still-legacy page, and the
    // fallback finds it by slug alone, preventing a spurious redirect to the default page.
    const currentPage = isRootRoute
      ? (defaultPage ?? null)
      : currentPageSlug
        ? (findEventPageByPageSlug(filteredPages, currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug) ??
          (isCustomPageSlug ? findEventPageByPageSlug(filteredPages, currentPageSlug) : null))
        : null;
    // CORE-6470: a custom page is any non-legacy kind (systemCustom | userCustom).
    const isCustomPage = !!currentPage && currentPage.pageKind !== PageKind.systemLegacy;
    return {
      currentPage,
      isCustomPage,
    };
  }, [currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug, filteredPages, isRootRoute, defaultPage]);

  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPage?.pageSlug);
  const { isWelcomePage, isCustomWelcomePage } = useMemo(
    () => checkIsWelcomePage(currentPage?.pageSlug, currentPage?.type),
    [checkIsWelcomePage, currentPage?.pageSlug, currentPage?.type],
  );
  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  const queryParams = useQueryParams();

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (currentPageSlug === 'registrylinks' || currentPageSlug === 'registry') {
      // Wait for migration flags before deciding — without this, the flag-aware filter
      // treats the loading state as flag-off and bounces a migrated event to /registry.
      if (isMigratedFlagsLoading) return;
      // Wait for the remote rollout flag too. Without this, treatment users land the redirect
      // before Amplitude resolves and get bounced to /registry irrecoverably.
      if (isRegistryLinksNavLoading) return;
      // Skip the legacy /registrylinks → /registry redirect when a migrated registry custom page
      // exists for this event, OR when registryEnableGuestNavRegistryLinks is on (the rollout
      // intentionally keeps the user on /registrylinks instead of bouncing them to the
      // standalone registry items app).
      const hasMigratedRegistryLinksPage = filteredPages.some(page => page.pageKind !== PageKind.systemLegacy && toCanonicalMigratedSlug(page.pageSlug) === 'registrylinks');
      if (!hasMigratedRegistryLinksPage && !useRegistryLinksNav) {
        const path = queryString.stringifyUrl({
          url: `/${eventHandle}/registry`,
          query: queryParams,
        });
        window.location.href = path;
        return;
      }
    }

    // Wait for feature flags to load before determining redirect
    // This prevents race conditions where defaultPage is undefined because flags are still loading
    if (isWelcomePageCheckingLoading || isDefaultPageLoading) {
      return;
    }

    // Don't redirect if user has already redirected
    if (hasRedirectedRef.current) {
      return;
    }

    // Don't redirect for root route - it will render the default page
    // Only redirect for invalid slugs (slug provided but page not found)
    if (!currentPage && !isRootRoute && defaultPage) {
      // Redirect to root URL (which will render the default page)
      const path = eventPageRoute.goToPath(defaultPage.type, defaultPage.pageSlug);

      if (path) {
        hasRedirectedRef.current = true;

        history.replace(
          queryString.stringifyUrl({
            url: path,
            query: queryParams,
          }),
        );
      }
    }
  }, [
    history,
    eventHandle,
    currentPage,
    currentPageSlug,
    queryParams,
    defaultPage,
    filteredPages,
    eventPageRoute,
    isWelcomePageCheckingLoading,
    isDefaultPageLoading,
    isMigratedFlagsLoading,
    isRootRoute,
    useRegistryLinksNav,
    isRegistryLinksNavLoading,
  ]);

  const { isSmallScreen, reapplyDesign, loading } = useBrannanDesign(eventHandle, eventDesign);

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, currentPage]);

  // Hide page photo for the welcome page when websiteHeader is present
  const shouldHidePagePhoto = isWelcomePage && websiteHeader;

  // To reduce the likelihood of preview falling out of sync in website designer,
  // we'll use the themeId as a key to force remount the Shell component
  const shellKey = isPreviewing ? event.eventDesign?.theme.themeId || 'default' : undefined;

  const shouldHideColumn = useMemo(() => {
    return isNativeAccommodationPage && !currentPage?.subTitle;
  }, [isNativeAccommodationPage, currentPage?.subTitle]);

  useScrollToTop({ behavior: 'instant' });

  return (
    <BrannanDesignProvider value={{ reapplyDesign }}>
      {/*  TODO: Question do we need to add !isNativeAccommodations check here - https://withjoy.atlassian.net/browse/PRNT-3157 */}
      {websiteHeader && !hideNav && (
        <StandaloneWebsiteHeader
          websiteHeader={websiteHeader}
          navBarVariant={isSmallScreen ? 'menu' : 'inline'}
          pages={filteredPages}
          currentPageType={currentPage?.type}
          eventHandle={eventHandle}
          handleMenuClicked={handleMenuClicked}
          eventProps={eventMenuProps}
          pageSlug={currentPage?.pageSlug}
          eventDesign={eventDesign}
        />
      )}
      <Shell
        eventHandle={eventHandle}
        eventDesign={event.eventDesign}
        isCustomPage={isCustomPage}
        key={shellKey}
      >
        {/* WebsiteHeader is above the fold — render during SSR */}
        {!isNativeAccommodationPage && !hideNav && (
          <WebsiteHeader
            data-testid={'layout-header'}
            websiteHeader={websiteHeader}
            applicatorProp={componentTitleBanner.prop}
            navBarVariant={isSmallScreen ? 'menu' : 'inline'}
            eventHandle={eventHandle}
            pages={filteredPages}
            info={info}
            handleMenuClicked={handleMenuClicked}
            handleEditEventClicked={handleEditEventClicked}
            eventProps={eventMenuProps}
            page={currentPage?.type}
            pageSlug={currentPage?.pageSlug}
            pageId={currentPage?.id}
            eventDisplayName={info?.eventDisplayName}
          />
        )}
        {/* Body chrome (photo, title, subtitle) SSRs; the PageWidget inside defers
            via NoSsr — see doc/improve-guest-site-perf/15-ssr-narrow-surface/PLAN.md */}
        <Body
          data-testid={`layout-body-${currentPageSlug}`}
          marginTop={[0, null, 9]}
          page={currentPage}
          event={event}
          eventHandle={eventHandle}
          isWelcomePage={isWelcomePage}
          isCustomWelcomePage={isCustomWelcomePage}
          photo={
            isWelcomePage && event.video ? (
              <ColumnsV2.Column
                style={isCustomPage ? { display: shouldHideColumn ? 'none' : 'block' } : {}}
                span={[12, 10]}
                offset={[0, 1]}
              >
                <LazyWelcomeVideo
                  video={event.video}
                  thumbnailUrl={event.video.thumbnailUrl || welcomePhotoUrl}
                />
              </ColumnsV2.Column>
            ) : shouldHidePagePhoto ? null : (
              <CoverPhotoWrapper
                renderFullColumn={currentPage?.photoPresentation?.presentationLayout !== 'center'}
                isCustomPage={isCustomPage}
                isNativeAccommodations={isNativeAccommodationPage}
                subTitle={currentPage?.subTitle}
              >
                <CoverPhoto
                  websiteLayoutType={DesignLayoutType.brannan}
                  photo={currentPage?.photoV2}
                  pageId={currentPage?.id}
                  pageType={currentPage?.type}
                  pageSlug={currentPage?.pageSlug}
                  photoPresentation={currentPage?.photoPresentation}
                />
              </CoverPhotoWrapper>
            )
          }
        />
        <NoSsr>
          <Flex __css={brannanFooterContainerStyles}>
            {/* FooterNav is only visible on mobile */}
            {isGuestMobileFooterNavEnabled && !hideNav && (
              <Flex
                width="100%"
                display={{ _: 'flex', sm2: 'none' }}
              >
                <FooterNav
                  pages={filteredPages}
                  eventHandle={eventHandle}
                  eventDisplayName={info?.eventDisplayName}
                  handleMenuClicked={handleMenuClicked}
                />
              </Flex>
            )}
            <Footer
              onButtonClicked={handleFooterButtonClicked}
              data-testid={'layout-footer'}
            />
          </Flex>
        </NoSsr>
      </Shell>
    </BrannanDesignProvider>
  );
};

LayoutBrannan.displayName = 'LayoutBrannan';

export { LayoutBrannan };
